import { useState, useEffect, useRef, useCallback } from 'react'
import { Send, Loader2, ChevronRight, Paperclip, X, File, AlertCircle, Bot, Save, Link2, TrendingUp, Clapperboard, Building2, Lightbulb, Archive, ChevronDown, ChevronUp, Clock, Eye, Minus, Trash2, CornerUpRight } from 'lucide-react'
import { Modal } from '../ui/Modal'
import RichTextEditor from '../ui/RichTextEditor'
import EmailRecipientField from '../ui/EmailRecipientField'
import MultiEntityPicker from '../ui/MultiEntityPicker'
import { useToast } from '../../contexts/ToastContext'
import { useCompose } from '../../contexts/ComposeContext'
import { dealLabel } from '../../lib/dealLabel'
import { queuedWrite } from '../../lib/queuedWrite'
import { getSessionWithTimeout } from '../../lib/authSession'
import { enqueueSend } from '../../lib/sendEmailFlush'
import { stripTrackingArtifacts } from '../../lib/emailTrackingStrip'
import {
  UNDO_SEND_MS, draftKeyFor, newSendKey,
  joinBodyAndQuote, splitBodyAndQuote,
  localAttachment, attachmentsForWire, adoptDraftAttachmentRefs, attachmentNeedsReattach,
  snapshotCompose, hasComposeContent,
  classifySendOutcome, interpretDraftSave,
} from '../../lib/composeDraft'
import { writeDevice, clearDevice, readDevice, writePendingSend, clearPendingSend } from '../../lib/composeStore'

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024
const AUTOSAVE_MS = 2000

const JANET_SIG_MARKER = '<br><div style="margin-top:16px;border-top:1px solid #e5e7eb;padding-top:12px">'
function stripJanetSig(html) {
  if (!html) return ''
  const idx = html.indexOf(JANET_SIG_MARKER)
  return idx >= 0 ? html.slice(0, idx) : html
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const raw = reader.result
      const base64 = String(raw).split(',')[1] || ''
      resolve(base64)
    }
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`))
    reader.readAsDataURL(file)
  })
}

function humanBytes(n) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function htmlToText(html) {
  if (!html) return ''
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

function isNetworkError(e) {
  const msg = e?.message || ''
  return (e?.name === 'TypeError' && /fetch|network/i.test(msg)) || e?.name === 'AbortError'
}

const recipientsString = v => Array.isArray(v)
  ? v.map(r => (typeof r === 'string' ? r : r?.email)).filter(Boolean).join(', ')
  : (v || '')

async function bearer(timeout = 3000) {
  const r = await getSessionWithTimeout(timeout)
  if (r.timedOut) return null
  return r.data?.session?.access_token || null
}

export default function ComposeEmailModal({ isOpen, onClose, prefill, docked = false, minimized = false, onToggleMinimize, windowId }) {
  const toast = useToast()
  const { openCompose } = useCompose()
  const draftKey = draftKeyFor(windowId)
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [quotedHtml, setQuotedHtml] = useState('')
  const [quotedVisible, setQuotedVisible] = useState(true)
  const [attachments, setAttachments] = useState([])
  const [attaching, setAttaching] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')
  // Per-recipient ("split") tracking — send a separate tracked copy to each To
  // recipient so opens are attributable to a specific person. Only meaningful for
  // a new multi-recipient compose; recipients won't see one another.
  const [trackEach, setTrackEach] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [inReplyTo, setInReplyTo] = useState(null)
  const [threadId, setThreadId] = useState(null)
  const [emailIntelligenceId, setEmailIntelligenceId] = useState(null)
  const [gmailDraftId, setGmailDraftId] = useState(null)
  // The Gmail draft message id this window last wrote or loaded. Gmail gives
  // every saved version a new id, so a different id in Gmail means the draft
  // was edited there — and that edit wins (api/_lib/gmail-drafts.js).
  const [gmailDraftMessageId, setGmailDraftMessageId] = useState(null)
  // 'idle' | 'saving' | 'saved' | 'error' | 'conflict' | 'missing' | 'local'
  const [saveState, setSaveState] = useState('idle')
  const [saveError, setSaveError] = useState(null)
  const [conflictCurrent, setConflictCurrent] = useState(null)
  // Non-content context carried from the prefill (Janet draft, send-as
  // address, signature suppression, suggested Cc) — kept in the device copy so
  // a restored window behaves the same.
  const [ctx, setCtx] = useState(null)
  const [notice, setNotice] = useState(null) // { tone, text } — why this window came back
  // A reopened unconfirmed send keeps its key: sending again reports the
  // earlier copy if it went out instead of sending a second one.
  const [resumeSendKey, setResumeSendKey] = useState(null)
  const [linkedDeals, setLinkedDeals]         = useState([])
  const [linkedProjects, setLinkedProjects]   = useState([])
  const [linkedProperties, setLinkedProperties] = useState([])
  const [linkedOpps, setLinkedOpps]           = useState([])
  // Entity link section collapsed by default for replies (saves vertical space)
  const [linksExpanded, setLinksExpanded]     = useState(false)
  const draftTimer = useRef(null)
  const fileInputRef = useRef(null)
  const hydrated = useRef(false)
  const saveSeq = useRef(0)
  // Whether this session restored from the device copy (shown as a banner)
  const [restoredDraft, setRestoredDraft] = useState(false)

  const isReply = !!inReplyTo
  const toCount = (to || '').split(',').map(s => s.trim()).filter(Boolean).length
  // Split-tracking only applies to a brand-new compose with >1 recipient
  const canTrackEach = !isReply && toCount > 1
  // Gmail autosave writes into the signed-in person's own mailbox. A Janet
  // draft sent as another address, and a personal iCloud reply, are kept on
  // this device only.
  const isIcloud = String(threadId || inReplyTo || '').startsWith('icloud:')
  const gmailSaveEnabled = !ctx?.sendAsEmail && !isIcloud

  const currentSnapshot = useCallback(() => snapshotCompose({
    to, cc, bcc, subject, bodyHtml, quotedHtml, quotedVisible, inReplyTo, threadId,
    emailIntelligenceId, gmailDraftId, gmailDraftMessageId, attachments, context: ctx,
  }), [to, cc, bcc, subject, bodyHtml, quotedHtml, quotedVisible, inReplyTo, threadId, emailIntelligenceId, gmailDraftId, gmailDraftMessageId, attachments, ctx])

  const applySnapshot = (snap) => {
    setTo(snap.to || ''); setCc(snap.cc || ''); setBcc(snap.bcc || '')
    setShowCc(!!snap.cc); setShowBcc(!!snap.bcc)
    setSubject(snap.subject || '')
    setBodyHtml(snap.bodyHtml || '')
    setQuotedHtml(snap.quotedHtml || '')
    setQuotedVisible(snap.quotedVisible !== false && !!snap.quotedHtml)
    setInReplyTo(snap.inReplyTo || null)
    setThreadId(snap.threadId || null)
    setEmailIntelligenceId(snap.emailIntelligenceId || null)
    setGmailDraftId(snap.gmailDraftId || null)
    setGmailDraftMessageId(snap.gmailDraftMessageId || null)
    setAttachments(snap.attachments || [])
    setCtx(snap.context || null)
    setLinksExpanded(!snap.inReplyTo)
  }

  // Device copy on every change — every compose, including replies, forwards
  // and Janet prefills. The copy is the last line of defence for a reload,
  // a crash, or a Gmail save that failed.
  useEffect(() => {
    if (!isOpen || !hydrated.current) return
    const snap = currentSnapshot()
    if (!hasComposeContent(snap)) return
    writeDevice(draftKey, snap)
  }, [isOpen, draftKey, currentSnapshot])

  // Reset/hydrate on open
  useEffect(() => {
    if (!isOpen) return
    hydrated.current = false
    setResumeSendKey(null)
    setSaveState('idle'); setSaveError(null); setConflictCurrent(null); setNotice(null)
    const saved = readDevice(draftKey)
    if (prefill?.restoreSnapshot) {
      // Undo Send / a rejected or unconfirmed send: the complete window comes back.
      applySnapshot(prefill.restoreSnapshot)
      setNotice(prefill.notice || null)
      setResumeSendKey(prefill.resumeSendKey || null)
      setRestoredDraft(false)
      if (prefill.clearPendingKey) clearPendingSend(prefill.clearPendingKey)
    } else if (saved && hasComposeContent(saved)) {
      // This window had state before a reload — that wins over the prefill
      // that first opened it.
      applySnapshot(saved)
      setRestoredDraft(true)
      const lost = (saved.attachments || []).filter(attachmentNeedsReattach)
      if (lost.length) setNotice({ tone: 'warn', text: `Add again: ${lost.map(a => a.filename).join(', ')} — too large to keep on this device before it reached Gmail.` })
    } else if (prefill) {
      const ctxFields = {
        sendAsEmail: prefill.sendAsEmail || null,
        fromAddress: prefill.fromAddress || null,
        suppressSignature: !!prefill.suppressSignature,
        isJanetDraft: !!prefill.isJanetDraft,
        janetOriginalBody: prefill.janetOriginalBody || null,
        janetDraftType: prefill.janetDraftType || null,
        janetCpTier: prefill.janetCpTier || null,
        suggestedCc: prefill.suggestedCc || [],
      }
      applySnapshot(snapshotCompose({
        to: recipientsString(prefill.to),
        cc: recipientsString(prefill.cc),
        bcc: recipientsString(prefill.bcc),
        subject: prefill.subject || '',
        bodyHtml: prefill.bodyHtml || prefill.body || '',
        // Strip tracking pixels/links from the quoted original — otherwise the
        // recipient opening this reply re-fires the original email's tracker.
        quotedHtml: stripTrackingArtifacts(prefill.quotedHtml) || '',
        quotedVisible: !!prefill.quotedHtml,
        inReplyTo: prefill.inReplyTo || null,
        threadId: prefill.threadId || null,
        emailIntelligenceId: prefill.emailIntelligenceId || null,
        gmailDraftId: prefill.gmailDraftId || null,
        gmailDraftMessageId: prefill.gmailDraftMessageId || null,
        attachments: prefill.attachments || [],
        context: ctxFields,
      }))
      setRestoredDraft(false)
    } else {
      applySnapshot(snapshotCompose({}))
      setRestoredDraft(false)
    }
    setLinkedDeals([])
    setLinkedProjects([])
    setLinkedProperties([])
    setLinkedOpps([])
    setTrackEach(false)
    hydrated.current = true
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prefill])

  // ── Gmail draft save ──────────────────────────────────────────────────────
  // Returns the save outcome kind. Never overwrites a newer Gmail edit, never
  // recreates a draft that disappeared unless the person asks (`opts`).
  const saveToGmail = useCallback(async (opts = {}) => {
    const snap = currentSnapshot()
    if (!hasComposeContent(snap)) return 'empty'
    if (!gmailSaveEnabled) { setSaveState('local'); return 'local' }
    const seq = ++saveSeq.current
    setSaveState('saving')
    try {
      const token = await bearer()
      if (!token) throw new Error('Session check timed out')
      const resp = await fetch('/api/save-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: snap.to, cc: snap.cc, bcc: snap.bcc, subject: snap.subject,
          bodyHtml: joinBodyAndQuote(snap.bodyHtml, snap.quotedHtml, snap.quotedVisible),
          inReplyTo: snap.inReplyTo, threadId: snap.threadId,
          gmailDraftId: opts.asNew ? null : snap.gmailDraftId,
          baseMessageId: opts.asNew ? null : snap.gmailDraftMessageId,
          attachments: attachmentsForWire(snap.attachments),
          allowRecreate: !!opts.allowRecreate,
          force: !!opts.force,
        }),
      })
      const body = await resp.json().catch(() => ({}))
      const out = interpretDraftSave(resp.status, body)
      if (seq !== saveSeq.current) return out.kind // a newer save superseded this one
      if (out.kind === 'saved') {
        setGmailDraftId(out.draftId)
        setGmailDraftMessageId(out.messageId)
        if (out.attachments) setAttachments(prev => adoptDraftAttachmentRefs(prev, out.attachments))
        setSaveState('saved'); setSaveError(null); setConflictCurrent(null)
      } else if (out.kind === 'conflict') {
        setSaveState('conflict'); setConflictCurrent(out.current)
      } else if (out.kind === 'missing') {
        setSaveState('missing')
      } else {
        setSaveState('error'); setSaveError(out.message)
      }
      return out.kind
    } catch (e) {
      if (seq === saveSeq.current) { setSaveState('error'); setSaveError(e.message) }
      return 'error'
    }
  }, [currentSnapshot, gmailSaveEnabled])

  // Autosave to Gmail drafts — every compose. Paused while a conflict or a
  // missing draft waits for the person's choice.
  useEffect(() => {
    if (!isOpen || !hydrated.current) return
    if (saveState === 'conflict' || saveState === 'missing') return
    if (draftTimer.current) clearTimeout(draftTimer.current)
    draftTimer.current = setTimeout(() => { saveToGmail() }, AUTOSAVE_MS)
    return () => { if (draftTimer.current) clearTimeout(draftTimer.current) }
  // saveState deliberately excluded: a finished save must not schedule another.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, to, cc, bcc, subject, bodyHtml, quotedHtml, quotedVisible, attachments.length])

  const loadGmailVersion = async () => {
    if (!gmailDraftId) return
    try {
      const token = await bearer(5000)
      const resp = await fetch('/api/list-drafts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ draft_id: gmailDraftId }),
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const d = await resp.json()
      const { bodyHtml: b, quotedHtml: q } = splitBodyAndQuote(d.body_html || '')
      applySnapshot(snapshotCompose({
        ...currentSnapshot(),
        to: d.to || '', cc: d.cc || '', bcc: d.bcc || '', subject: d.subject || '',
        bodyHtml: b, quotedHtml: q, quotedVisible: !!q,
        threadId: d.thread_id || threadId, inReplyTo: d.in_reply_to || inReplyTo,
        gmailDraftId: d.draft_id, gmailDraftMessageId: d.message_id,
        attachments: (d.attachments || []).map(a => ({ ...a, id: `${a.messageId}:${a.attachmentId || a.filename}` })),
      }))
      setSaveState('saved'); setConflictCurrent(null)
      toast.success('Loaded the version from Gmail')
    } catch (e) {
      toast.error(`Couldn't load the Gmail version: ${e.message}`)
    }
  }

  const onPickFiles = async (e) => {
    const fileList = Array.from(e.target.files || [])
    if (fileList.length === 0) return
    await addFiles(fileList)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const addFiles = async (files) => {
    setAttaching(true)
    try {
      const added = []
      for (const f of files) {
        if (f.size > MAX_ATTACHMENT_BYTES) { toast.error(`${f.name} is larger than 20 MB`); continue }
        const base64 = await fileToBase64(f)
        added.push(localAttachment({ filename: f.name, size: f.size, mimeType: f.type, base64 }))
      }
      if (added.length) setAttachments(prev => [...prev, ...added])
    } catch (err) {
      toast.error(err.message || 'Attachment failed')
    } finally {
      setAttaching(false)
    }
  }

  const removeAttachment = (i) => setAttachments(prev => prev.filter((_, idx) => idx !== i))

  const totalAttachmentBytes = attachments.reduce((sum, a) => sum + (a.size || 0), 0)

  const buildSendPayload = () => {
    const payload = {
      to: to.trim(),
      subject: subject.trim(),
      html: joinBodyAndQuote(bodyHtml, quotedHtml, quotedVisible),
      text: htmlToText(bodyHtml) + (quotedHtml && quotedVisible ? '\n\n' + htmlToText(quotedHtml) : ''),
      ...(ctx?.suppressSignature ? { injectSignature: false } : {}),
      ...(ctx?.sendAsEmail ? { sendAsEmail: ctx.sendAsEmail } : {}),
    }
    if (cc.trim()) payload.cc = cc.trim()
    if (bcc.trim()) payload.bcc = bcc.trim()
    if (inReplyTo) payload.inReplyTo = inReplyTo
    if (threadId) payload.threadId = threadId
    if (canTrackEach && trackEach) payload.trackIndividually = true
    if (attachments.length > 0) payload.attachments = attachmentsForWire(attachments)
    return payload
  }

  const buildQueueArgs = (payload, sendKey) => ({
    ...payload,
    sendKey,
    linkedDeals:      linkedDeals.map(x => x.id),
    linkedProjects:   linkedProjects.map(x => x.id),
    linkedProperties:    linkedProperties.map(x => x.id),
    linkedOpps:          linkedOpps.map(x => x.id),
    gmailDraftId:        gmailDraftId || null,
    emailIntelligenceId: emailIntelligenceId || null,
  })

  // Give the person their message back in a fresh window — Undo, or a send
  // Gmail refused. The pending copy is cleared only once the window holds it.
  const reopenWith = (snapshot, sendKey, notice) => {
    // The new window clears the pending copy when it mounts holding it
    // (clearPendingKey below) — never here, before it exists.
    openCompose({ restoreSnapshot: snapshot, notice, clearPendingKey: sendKey })
  }

  // doActualSend — the real API call, made after the undo window. The send
  // key travels with every attempt (here and in the offline queue), so a
  // retry after a lost response never produces a second email.
  const doActualSend = async (payload, queueArgs, snapshot, { archiveAfterSend = false, token } = {}) => {
    const sendKey = queueArgs.sendKey
    const sendingToastId = toast.loading('Sending…')
    const queueIt = async (why) => {
      await enqueueSend(queueArgs)
      writePendingSend(sendKey, snapshot, 'queued')
      toast.remove(sendingToastId)
      toast.success(`${why} — send queued; it will retry automatically and cannot go out twice.`)
    }
    // Personal iCloud reply — the thread/inReplyTo carry an 'icloud:' prefix.
    // These go to the worker's SMTP outbox, never Gmail, and never capture
    // Janet voice-training (personal mail is Janet-blind).
    const isIcloudReply = String(payload?.threadId || payload?.inReplyTo || '').startsWith('icloud:')
    try {
      const accessToken = token || await bearer()
      if (!accessToken) { await queueIt('Session was slow'); return }

      // iCloud: route to the SMTP outbox and stop — no Gmail send, no entity
      // links, no voice-training capture. The modal is already closed.
      if (isIcloudReply) {
        const r = await window.fetch('/api/inbox-actions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({
            action: 'send_icloud_compose',
            to: payload.to, cc: payload.cc, bcc: payload.bcc,
            subject: payload.subject, html: payload.html, text: payload.text,
            inReplyTo: payload.inReplyTo, threadId: payload.threadId,
            emailIntelligenceId: queueArgs.emailIntelligenceId, archiveAfterSend,
          }),
        })
        if (!r.ok) {
          const e = await r.json().catch(() => ({}))
          throw new Error(e.error || `Send failed (${r.status})`)
        }
        clearPendingSend(sendKey)
        toast.remove(sendingToastId)
        toast.success(archiveAfterSend ? 'Reply queued and archived' : 'Reply queued')
        return
      }

      const res = await window.fetch('/api/send-gmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        // A send reopened after a crash may already have gone out: ask the
        // server to look in Sent for this key first.
        body: JSON.stringify({ ...payload, sendKey, ...(queueArgs.resumed ? { sendKeyCheck: true } : {}) }),
      })
      const outcome = classifySendOutcome({ ok: res.ok, status: res.status })
      if (outcome === 'retry') { await queueIt(`Server hiccup (${res.status})`); return }
      if (outcome === 'rejected') {
        const errData = await res.json().catch(() => ({}))
        toast.remove(sendingToastId)
        toast.error(`Not sent: ${errData.error || `HTTP ${res.status}`}. Your message is back in a compose window.`)
        reopenWith(snapshot, sendKey, { tone: 'error', text: `Not sent — ${errData.error || `HTTP ${res.status}`}.` })
        return
      }

      const sendResult = await res.json().catch(() => ({}))
      clearPendingSend(sendKey)
      const sentMessageId = sendResult?.gmail_message_id || sendResult?.messageId
      const hasLinks = queueArgs.linkedDeals?.length || queueArgs.linkedProjects?.length || queueArgs.linkedProperties?.length || queueArgs.linkedOpps?.length

      if (sentMessageId && hasLinks) {
        fetch('/api/inbox-actions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({
            action: 'link_entities_after_send',
            gmail_message_id: sentMessageId,
            deal_ids: queueArgs.linkedDeals,
            project_ids: queueArgs.linkedProjects,
            property_ids: queueArgs.linkedProperties,
            opportunity_ids: queueArgs.linkedOpps,
          }),
        }).catch(err => console.warn('[compose] link-entities failed:', err.message))
      }

      if (archiveAfterSend && queueArgs.emailIntelligenceId) {
        fetch('/api/inbox-actions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ action: 'archive_email', email_intelligence_id: queueArgs.emailIntelligenceId }),
        }).catch(err => console.warn('[compose] archive failed:', err.message))
      }

      // The Gmail draft goes only AFTER Gmail confirmed the send.
      if (queueArgs.gmailDraftId) {
        fetch('/api/inbox-actions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ action: 'delete_draft', gmail_draft_id: queueArgs.gmailDraftId }),
        }).catch(() => {})
      }

      toast.remove(sendingToastId)
      toast.success(sendResult?.alreadySent ? 'Already sent — no second copy went out' : (archiveAfterSend ? 'Sent and archived' : 'Email sent'))

      // Voice training: capture diff if this compose was opened from a Janet
      // draft (JanetDraftModal → "Open in Compose"). Same shape as
      // janet-queue-decide.js edit_send and JanetDraftModal.handleSend.
      // Strip the appended signature from both sides so the training pair
      // captures only the body text the user actually edited.
      const sctx = snapshot.context || {}
      const janetOriginal = stripJanetSig(sctx.janetOriginalBody || '')
      const finalBody = stripJanetSig(snapshot.bodyHtml)
      if (janetOriginal && finalBody && janetOriginal !== finalBody && sentMessageId) {
        // One classified evidence row (facts vs style, own voice vs sent-as),
        // recorded only now that Gmail returned a message id.
        fetch('/api/janet-voice-evidence', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ action: 'record', janetDraft: janetOriginal, sentHtml: finalBody, sentMessageId, emailType: sctx.janetDraftType || null, sentAs: sctx.sendAsEmail || null }),
        }).catch(() => { /* non-fatal */ })
        try {
          const { data: { session: _vs } } = await getSessionWithTimeout(5000)
          const user = _vs?.user
          if (user?.id) {
            await queuedWrite('va_decision_log', 'insert', {
              user_id: user.id,
              mode: 'external',
              authority_tier: 'T1',
              flow: 'compose_edit',
              original_body: janetOriginal,
              final_body: finalBody,
              final_action: 'edited_sent',
              edit_summary: `Edited ${sctx.janetDraftType || 'draft'} before sending`,
              model_version: 'claude-sonnet-5',
            })
          }
        } catch { /* non-fatal — schema mismatch on va_decision_log should not block training save */ }
      }
    } catch (e) {
      if (isNetworkError(e)) {
        // The request may or may not have reached Gmail. The queue retries with
        // the same key, and the server checks Sent first.
        try { await queueIt('Connection dropped'); return } catch { /* fall through */ }
      }
      toast.remove(sendingToastId)
      toast.error(`Not sent: ${e.message}. Your message is back in a compose window.`)
      reopenWith(snapshot, sendKey, { tone: 'error', text: `Not sent — ${e.message}.` })
    }
  }

  // Send: the window closes at once and Undo holds the message for
  // UNDO_SEND_MS before anything is handed to Gmail. The whole window is kept
  // under the send's own pending key until Gmail confirms it — a reload or a
  // crash in between brings it back as a draft, never loses it.
  const handleSend = async ({ archiveAfterSend = false } = {}) => {
    if (!to.trim()) { toast.error('Recipient email is required'); return }
    if (!subject.trim()) { toast.error('Subject is required'); return }
    const lost = attachments.filter(attachmentNeedsReattach)
    if (lost.length) { toast.error(`Add ${lost.map(a => a.filename).join(', ')} again before sending`); return }

    const sendKey = resumeSendKey || newSendKey()
    const snapshot = currentSnapshot()
    const payload = buildSendPayload()
    const queueArgs = { ...buildQueueArgs(payload, sendKey), ...(resumeSendKey ? { resumed: true } : {}) }

    writePendingSend(sendKey, snapshot, 'undo')
    if (draftTimer.current) clearTimeout(draftTimer.current)
    clearDevice(draftKey)
    onClose()

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      try {
        await enqueueSend(queueArgs)
        writePendingSend(sendKey, snapshot, 'queued')
        toast.success('Queued — will send when back online')
      } catch (e) {
        toast.error(`Couldn't queue: ${e.message}. Your message is back in a compose window.`)
        reopenWith(snapshot, sendKey, { tone: 'error', text: `Not sent — couldn't queue: ${e.message}.` })
      }
      return
    }

    // Pre-fetch auth token now so it's available in the timer callback
    const preToken = await bearer()

    // Undo window — the API call fires only after it. Timer is LOCAL to this
    // send: with multiple docked compose windows, two sends can overlap, and a
    // shared timer would let the second send silently cancel the first.
    let undoTimer = null
    const seconds = Math.round(UNDO_SEND_MS / 1000)
    const undoToastId = toast.action(
      archiveAfterSend ? `Sending & archiving in ${seconds}s…` : `Sending in ${seconds}s…`,
      'Undo',
      () => {
        if (!undoTimer) return
        clearTimeout(undoTimer); undoTimer = null
        reopenWith(snapshot, sendKey, { tone: 'info', text: 'Send undone — nothing went out.' })
        toast.success('Send undone')
      },
      UNDO_SEND_MS
    )
    undoTimer = setTimeout(async () => {
      undoTimer = null
      toast.remove(undoToastId)
      writePendingSend(sendKey, snapshot, 'sending')
      await doActualSend(payload, queueArgs, snapshot, { archiveAfterSend, token: preToken })
    }, UNDO_SEND_MS)
  }

  // Schedule send: save payload to DB for later delivery
  const handleScheduleSend = async () => {
    if (!to.trim()) { toast.error('Recipient email is required'); return }
    if (!subject.trim()) { toast.error('Subject is required'); return }
    if (!scheduledAt) { toast.error('Please pick a date and time'); return }
    const scheduledDate = new Date(scheduledAt)
    if (scheduledDate <= new Date()) { toast.error('Scheduled time must be in the future'); return }

    const payload = buildSendPayload()
    const queueArgs = buildQueueArgs(payload, newSendKey())

    try {
      const accessToken = await bearer()
      if (!accessToken) throw new Error('Session check timed out')

      const res = await fetch('/api/schedule-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ ...queueArgs, scheduled_at: scheduledDate.toISOString() }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `HTTP ${res.status}`)
      }
      clearDevice(draftKey)
      onClose()
      toast.success(`Scheduled for ${scheduledDate.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`)
    } catch (e) {
      toast.error(`Couldn't schedule: ${e.message}`)
    }
  }

  // Close keeps the work: the device copy is cleared only once Gmail holds the
  // latest version. If Gmail can't be reached the window comes back next load.
  const closeWindow = async () => {
    if (draftTimer.current) clearTimeout(draftTimer.current)
    const snap = currentSnapshot()
    if (!hasComposeContent(snap)) { clearDevice(draftKey); onClose(); return }
    if (saveState === 'conflict' || saveState === 'missing') {
      toast.success('Kept on this device — it reopens next time so you can choose.')
      onClose(); return
    }
    const kind = await saveToGmail()
    if (kind === 'saved') {
      clearDevice(draftKey)
      toast.success('Draft saved to Gmail')
    } else {
      toast.success('Kept on this device — not yet in Gmail; it reopens next time.')
    }
    onClose()
  }

  const handleSaveAsDraft = async () => {
    if (!hasComposeContent(currentSnapshot())) { toast.error('Nothing to save yet'); return }
    setSavingDraft(true)
    try {
      const kind = await saveToGmail()
      if (kind !== 'saved') {
        if (kind === 'conflict') throw new Error('This draft was changed in Gmail — choose which version to keep')
        if (kind === 'missing') throw new Error('This draft is no longer in Gmail')
        throw new Error(kind === 'local' ? 'This message is kept on this device only' : 'Gmail could not be reached')
      }
      clearDevice(draftKey)
      toast.success('Draft saved to Gmail')
      onClose()
    } catch (e) {
      toast.error(`Couldn't save draft: ${e.message}`)
    } finally {
      setSavingDraft(false)
    }
  }

  const discardDraft = async () => {
    if (hasComposeContent(currentSnapshot()) && !window.confirm('Discard this draft? It is removed here and from Gmail Drafts.')) return
    if (draftTimer.current) clearTimeout(draftTimer.current)
    saveSeq.current++ // drop any save in flight
    clearDevice(draftKey)
    if (gmailDraftId) {
      const token = await bearer()
      fetch('/api/inbox-actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
        body: JSON.stringify({ action: 'delete_draft', gmail_draft_id: gmailDraftId }),
      }).catch(() => {})
    }
    onClose()
  }

  // Suggested CC recipients from thread participants passed in prefill
  const suggestedCc = ctx?.suggestedCc || []

  const handleChipMoved = (email, fromFieldKey, toFieldKey) => {
    const removeEmail = (val, setVal) => {
      const updated = val.split(',').map(s => s.trim()).filter(s => s.toLowerCase() !== email.toLowerCase()).join(', ')
      setVal(updated)
    }
    if (fromFieldKey === 'to') removeEmail(to, setTo)
    else if (fromFieldKey === 'cc') removeEmail(cc, setCc)
    else if (fromFieldKey === 'bcc') removeEmail(bcc, setBcc)
    if (toFieldKey === 'cc') setShowCc(true)
    if (toFieldKey === 'bcc') setShowBcc(true)
  }

  // Drag-drop handlers
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true) }
  const onDragLeave = (e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false) }
  const onDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false)
    const files = Array.from(e.dataTransfer.files || [])
    if (files.length === 0) return
    await addFiles(files)
  }

  const saveLabel = {
    saving: 'Saving…',
    saved: 'Saved to Gmail Drafts',
    error: 'Not saved to Gmail — kept on this device',
    local: 'Kept on this device',
    conflict: 'Changed in Gmail',
    missing: 'No longer in Gmail',
  }[saveState] || ''


  const composeBody = (
      <div
        className={`p-4 md:p-6 space-y-3 relative transition-colors ${isDragging ? 'bg-navy-50/60' : ''}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* Drag-drop overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-navy-400 bg-navy-50/80 pointer-events-none">
            <div className="flex flex-col items-center gap-2 text-navy-600">
              <Paperclip size={28} />
              <p className="text-sm font-semibold">Drop files to attach</p>
            </div>
          </div>
        )}
        {/* Why this window came back (Undo, a refused send, an unconfirmed send) */}
        {notice && (
          <div role="status" className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${
            notice.tone === 'error' ? 'bg-red-50 border-red-200 text-red-800'
              : notice.tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-navy-50 border-navy-200 text-navy-700'}`}>
            <AlertCircle size={13} className="shrink-0 mt-0.5" />
            <span className="flex-1">{notice.text}</span>
            <button type="button" onClick={() => setNotice(null)} className="shrink-0 opacity-70 hover:opacity-100" aria-label="Dismiss notice"><X size={12} /></button>
          </div>
        )}

        {/* Restored draft banner */}
        {restoredDraft && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
            <span>Draft restored from your last session.</span>
            <button type="button" onClick={() => setRestoredDraft(false)} className="text-amber-600 hover:text-amber-900 underline shrink-0">OK</button>
          </div>
        )}

        {/* A newer version of this draft exists in Gmail — never overwritten silently */}
        {saveState === 'conflict' && (
          <div role="alert" className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-300 text-xs text-amber-900 space-y-1.5">
            <p><strong>This draft was changed in Gmail</strong>{conflictCurrent?.updatedAt ? ` (${new Date(conflictCurrent.updatedAt).toLocaleString()})` : ''}. Autosave is paused so neither version is lost.</p>
            {conflictCurrent?.snippet && <p className="italic text-amber-800 truncate">Gmail: “{conflictCurrent.snippet}”</p>}
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={loadGmailVersion} className="btn-secondary text-xs px-2.5 py-1">Use the Gmail version</button>
              <button type="button" onClick={() => saveToGmail({ asNew: true })} className="btn-secondary text-xs px-2.5 py-1">Keep mine as a separate draft</button>
              <button type="button" onClick={() => { if (window.confirm('Replace the Gmail version with this one?')) saveToGmail({ force: true }) }} className="btn-ghost text-xs px-2.5 py-1">Replace the Gmail version</button>
            </div>
          </div>
        )}
        {saveState === 'missing' && (
          <div role="alert" className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-300 text-xs text-amber-900 space-y-1.5">
            <p><strong>This draft is no longer in Gmail</strong> — it may have been sent or deleted there. Check Sent before sending again.</p>
            <button type="button" onClick={() => saveToGmail({ allowRecreate: true })} className="btn-secondary text-xs px-2.5 py-1">Save it as a new draft</button>
          </div>
        )}

        {/* Janet draft banner */}
        {ctx?.isJanetDraft && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-navy-50 border border-navy-200 text-xs text-navy-700">
            <Bot size={13} className="shrink-0 text-navy-500" />
            <span>Janet draft — sending as <strong>{ctx.fromAddress || ctx.sendAsEmail || 'janet@chatrone.com'}</strong>. Review before sending.</span>
          </div>
        )}

        {/* To */}
        <EmailRecipientField
          label="To"
          value={to}
          onChange={setTo}
          autoFocus
          placeholder="name or email@domain.com"
          fieldKey="to"
          onChipMoved={(email, from) => handleChipMoved(email, from, 'to')}
        />

        {/* CC / BCC toggle row */}
        {!showCc && !showBcc && (
          <div className="flex items-center gap-3 text-xs">
            <button onClick={() => setShowCc(true)} className="text-navy-500 hover:text-navy-700 flex items-center gap-1">
              <ChevronRight size={12} /> Add Cc
            </button>
            <button onClick={() => setShowBcc(true)} className="text-navy-500 hover:text-navy-700 flex items-center gap-1">
              <ChevronRight size={12} /> Add Bcc
            </button>
          </div>
        )}
        {showCc && (
          <EmailRecipientField
            label="Cc"
            value={cc}
            onChange={setCc}
            suggestedRecipients={suggestedCc}
            fieldKey="cc"
            onChipMoved={(email, from) => handleChipMoved(email, from, 'cc')}
          />
        )}
        {showBcc && (
          <EmailRecipientField
            label="Bcc"
            value={bcc}
            onChange={setBcc}
            fieldKey="bcc"
            onChipMoved={(email, from) => handleChipMoved(email, from, 'bcc')}
          />
        )}
        {showCc && !showBcc && (
          <button onClick={() => setShowBcc(true)} className="text-xs text-navy-500 hover:text-navy-700 flex items-center gap-1">
            <ChevronRight size={12} /> Add Bcc
          </button>
        )}

        {/* Subject */}
        <div>
          <label className="input-label">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="input-field w-full text-sm"
            placeholder="Email subject"
          />
        </div>

        {/* Body */}
        <div>
          <label className="input-label mb-1 block">Message</label>
          {(() => {
            // For Janet drafts: split editable body from the appended signature block.
            // TipTap strips <table> so the signature div must stay outside the editor.
            // Split marker matches what janet-draft.js appends server-side.
            const SIG_MARKER = '<br><div style="margin-top:16px;border-top:1px solid #e5e7eb;padding-top:12px">'
            if (ctx?.isJanetDraft && bodyHtml?.includes(SIG_MARKER)) {
              const splitIdx = bodyHtml.indexOf(SIG_MARKER)
              const editableBody = bodyHtml.slice(0, splitIdx)
              const sigHtml = bodyHtml.slice(splitIdx)
              return (
                <>
                  <RichTextEditor
                    value={editableBody}
                    onChange={(html) => setBodyHtml(html + sigHtml)}
                    placeholder="Write your message…"
                    minHeight={180}
                  />
                  <div
                    className="chatrone-rich-content px-3 pt-3 border-t border-warm-200"
                    dangerouslySetInnerHTML={{ __html: sigHtml.replace(SIG_MARKER, '<div>') }}
                  />
                </>
              )
            }
            return (
              <RichTextEditor
                value={bodyHtml}
                onChange={(html) => setBodyHtml(html)}
                placeholder={isReply ? 'Write your reply…' : 'Write your message…'}
                minHeight={220}
              />
            )
          })()}
        </div>

        {/* Quoted original */}
        {quotedHtml && (
          <div>
            <button
              type="button"
              onClick={() => setQuotedVisible(v => !v)}
              className="text-xs text-warm-500 hover:text-warm-800 mb-1.5 flex items-center gap-1"
            >
              <ChevronRight size={11} className={quotedVisible ? 'rotate-90' : ''} />
              {quotedVisible ? 'Hide' : 'Show'} original message
            </button>
            {quotedVisible && (
              <div
                className="chatrone-rich-content border-l-[3px] border-warm-300 pl-3 ml-1 text-xs text-warm-600 max-h-48 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: quotedHtml }}
              />
            )}
          </div>
        )}

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="space-y-1.5">
            <label className="input-label">Attachments ({attachments.length})</label>
            {attachments.map((a, i) => (
              <div key={a.id || `${a.filename}-${i}`} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${attachmentNeedsReattach(a) ? 'bg-amber-50 border-amber-300' : 'bg-warm-50 border-warm-200'}`}>
                {a.origin === 'forward' ? <CornerUpRight size={12} className="text-warm-500 shrink-0" /> : <File size={12} className="text-warm-500 shrink-0" />}
                <span className="text-xs font-medium text-warm-800 truncate flex-1">{a.filename}</span>
                {a.origin === 'forward' && <span className="text-[10px] text-warm-500 shrink-0">from the original</span>}
                {attachmentNeedsReattach(a) && <span className="text-[10px] text-amber-700 shrink-0">add again</span>}
                <span className="text-[10px] text-warm-500 shrink-0">{a.size ? humanBytes(a.size) : ''}</span>
                <button type="button" onClick={() => removeAttachment(i)} className="text-warm-400 hover:text-red-500 shrink-0" aria-label={`Remove ${a.filename}`}>
                  <X size={11} />
                </button>
              </div>
            ))}
            {totalAttachmentBytes > 20 * 1024 * 1024 && (
              <div className="flex items-center gap-1 text-[11px] text-amber-600">
                <AlertCircle size={10} /> Total {humanBytes(totalAttachmentBytes)} — Gmail limit is 25 MB.
              </div>
            )}
          </div>
        )}

        {/* Entity links — collapsible, collapsed by default for replies */}
        <div className="border border-warm-200 rounded-lg bg-warm-50/50 overflow-hidden">
          <button
            type="button"
            onClick={() => setLinksExpanded(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-warm-100/50 transition-colors"
          >
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-warm-600 uppercase tracking-wide">
              <Link2 size={11} /> Link this email to
              {(linkedDeals.length + linkedProjects.length + linkedProperties.length + linkedOpps.length) > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-navy-100 text-navy-600 text-[10px] font-bold normal-case tracking-normal">
                  {linkedDeals.length + linkedProjects.length + linkedProperties.length + linkedOpps.length}
                </span>
              )}
            </div>
            {linksExpanded ? <ChevronUp size={12} className="text-warm-400" /> : <ChevronDown size={12} className="text-warm-400" />}
          </button>
          {linksExpanded && (
            <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              <LinkPickerField
                icon={<TrendingUp size={12} className="text-warm-500" />}
                label="Deals"
                value={linkedDeals}
                onChange={setLinkedDeals}
                table="deals"
                searchCols={['title', 'studio_name', 'talent_name', 'counterpart']}
                labelFn={d => dealLabel(d, { context: 'global' })}
                selectCols="id, title, deal_type, studio_name, counterpart, talent_name, talent_type, status"
                placeholder="Search deals…"
              />
              <LinkPickerField
                icon={<Clapperboard size={12} className="text-warm-500" />}
                label="Projects"
                value={linkedProjects}
                onChange={setLinkedProjects}
                table="projects"
                searchCols={['title', 'logline']}
                labelFn={p => p.title || '(untitled project)'}
                selectCols="id, title, format, status"
                placeholder="Search projects…"
              />
              <LinkPickerField
                icon={<Building2 size={12} className="text-warm-500" />}
                label="Properties"
                value={linkedProperties}
                onChange={setLinkedProperties}
                table="properties"
                searchCols={['title']}
                labelFn={p => p.title || '(untitled property)'}
                selectCols="id, title, status"
                placeholder="Search properties…"
              />
              <LinkPickerField
                icon={<Lightbulb size={12} className="text-warm-500" />}
                label="Opportunities"
                value={linkedOpps}
                onChange={setLinkedOpps}
                table="opportunities"
                searchCols={['title']}
                labelFn={o => o.title || '(untitled opportunity)'}
                selectCols="id, title, status"
                placeholder="Search opportunities…"
              />
            </div>
          )}
        </div>

        {/* Per-recipient tracking toggle — only for a new multi-recipient compose */}
        {canTrackEach && (
          <label className="flex items-start gap-2 px-3 py-2 rounded-lg bg-warm-50 border border-warm-200 cursor-pointer hover:bg-warm-100/60 transition-colors">
            <input
              type="checkbox"
              checked={trackEach}
              onChange={(e) => setTrackEach(e.target.checked)}
              className="mt-0.5 rounded border-warm-300 text-green-600 focus:ring-green-400 shrink-0"
            />
            <span className="text-xs leading-snug">
              <span className="flex items-center gap-1 font-semibold text-warm-800">
                <Eye size={12} className="text-green-600" /> Track each recipient individually
              </span>
              <span className="text-warm-500">
                Sends a separate copy to each of the {toCount} recipients so you can see who opened it.
                Recipients <strong>won't see each other</strong> (no shared To/Cc).
              </span>
            </span>
          </label>
        )}

        {/* Where this draft is kept right now — its own line so it never squeezes the buttons */}
        {saveLabel && (
          <p role="status" className={`text-[11px] ${saveState === 'error' || saveState === 'conflict' || saveState === 'missing' ? 'text-amber-700' : 'text-warm-500'}`} title={saveError || ''}>
            {saveLabel}
          </p>
        )}

        {/* Bottom bar */}
        <div className="flex items-center gap-2 pt-1 flex-wrap">
          <input ref={fileInputRef} type="file" multiple onChange={onPickFiles} className="hidden" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={attaching}
            className="btn-secondary text-sm px-3 py-2"
            title="Attach files (or drag & drop onto this window)"
          >
            {attaching ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
            Attach
          </button>

          {/* Schedule send toggle */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowSchedule(v => !v)}
              className={`btn-secondary text-sm px-3 py-2 ${showSchedule ? 'bg-navy-100 border-navy-300' : ''}`}
              title="Schedule send"
            >
              <Clock size={14} />
              Schedule
            </button>
            {showSchedule && (
              <div className="absolute bottom-full mb-2 left-0 bg-white border border-warm-200 rounded-lg shadow-xl p-3 z-30 w-64">
                <p className="text-[11px] font-semibold text-warm-600 uppercase tracking-wide mb-2">Schedule send</p>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  className="input-field w-full text-sm mb-2"
                />
                <button
                  type="button"
                  onClick={handleScheduleSend}
                  disabled={!scheduledAt || !to.trim() || !subject.trim()}
                  className="btn-primary text-sm w-full px-3 py-1.5"
                >
                  <Clock size={13} />
                  Schedule Send
                </button>
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={discardDraft} className="btn-ghost text-sm px-2 py-2" title="Discard this draft (also removes it from Gmail Drafts)" aria-label="Discard draft">
              <Trash2 size={14} />
            </button>
            <button onClick={closeWindow} className="btn-secondary text-sm px-4 py-2" title="Close — the draft stays in Gmail Drafts">
              Close
            </button>
            <button
              onClick={handleSaveAsDraft}
              disabled={savingDraft || !gmailSaveEnabled || !hasComposeContent(currentSnapshot())}
              className="btn-secondary text-sm px-4 py-2"
              title="Save to Gmail Drafts and close"
            >
              {savingDraft ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save as Draft
            </button>
            {isReply && (
              <button
                onClick={() => handleSend({ archiveAfterSend: true })}
                disabled={!to.trim() || !subject.trim()}
                className="btn-secondary text-sm px-4 py-2"
                title="Send and remove from inbox"
              >
                <Archive size={14} />
                Send + Archive
              </button>
            )}
            <button
              onClick={() => handleSend()}
              disabled={!to.trim() || !subject.trim()}
              className="btn-primary text-sm px-4 py-2"
            >
              <Send size={14} />
              Send
            </button>
          </div>
        </div>
      </div>
  )

  // Docked presentation (Gmail-style): a bottom-anchored window managed by
  // ComposeProvider — minimizable to a title bar, up to three side by side.
  // Closing saves to Gmail first; the device copy is cleared only once Gmail
  // holds the latest version, so the window doesn't resurrect needlessly but
  // also never loses unsaved work.
  if (docked) {
    if (!isOpen) return null
    const dockTitle = subject?.trim() || (isReply ? 'Reply' : 'New Message')
    return (
      <div
        className={`pointer-events-auto flex flex-col bg-white border border-warm-300 border-b-0 rounded-t-xl shadow-2xl overflow-hidden ${
          minimized ? 'w-64' : 'w-[min(95vw,480px)]'
        }`}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={onToggleMinimize}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleMinimize?.() } }}
          className="flex items-center gap-2 px-3 py-2 bg-navy-700 hover:bg-navy-600 text-white cursor-pointer select-none"
          title={minimized ? 'Restore' : 'Minimize'}
        >
          <span className="truncate flex-1 text-sm font-medium">{dockTitle}</span>
          {minimized ? <ChevronUp size={14} className="shrink-0 opacity-80" /> : <Minus size={14} className="shrink-0 opacity-80" />}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); closeWindow() }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); closeWindow() } }}
            className="shrink-0 p-1 -m-0.5 rounded hover:bg-navy-500 transition-colors"
            title="Close — draft auto-saves to Gmail Drafts"
            aria-label="Close compose window"
          >
            <X size={14} />
          </span>
        </div>
        {!minimized && (
          <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: 'min(72vh, 640px)' }}>
            {composeBody}
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={closeWindow} title={isReply ? 'Reply' : 'Compose Email'} size="lg">
      {composeBody}
    </Modal>
  )
}

function LinkPickerField({ icon, label, value, onChange, table, searchCols, labelFn, selectCols, placeholder }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 text-[11px] font-medium text-warm-600">
        {icon}
        <span>{label}</span>
      </div>
      <MultiEntityPicker
        value={value}
        onChange={onChange}
        table={table}
        searchCols={searchCols}
        labelFn={labelFn}
        selectCols={selectCols}
        placeholder={placeholder}
        limit={8}
      />
    </div>
  )
}
